// src/kegops-dashboard.jsx
// ============================================================
// DASHBOARD VIEW - Main dashboard component
// ============================================================
// Displays the main dashboard with tabs for:
// - Kegs: Filterable table of all kegs
// - On Tap: Beers currently being served
// - On Deck: Beers conditioning/lagering/carbonating
// - Previous Beers: Historical beer data with ratings
//
// REFACTORED: Tab content extracted to components/dashboard/*
// for improved maintainability and AI comprehension.
// ============================================================

import React, { useEffect, useRef, useState } from 'react';
import { Search, Plus, User, Settings, LogOut, ChevronDown } from 'lucide-react';
import { TAP_STATUSES, CLEANING_STATUSES, getSelectableLocations } from './kegops-utils';
import { supabase } from './supabaseClient';

// Import extracted tab components
import { DashboardKegsTab } from './components/dashboard/DashboardKegsTab';
import { DashboardBeersTab } from './components/dashboard/DashboardBeersTab';
import { DashboardOnDeckTab } from './components/dashboard/DashboardOnDeckTab';
import { DashboardHistoryTab } from './components/dashboard/DashboardHistoryTab';
import DashboardMaintenanceTab from './components/dashboard/DashboardMaintenanceTab';

// ============================================================
// SUB-COMPONENTS
// ============================================================

/**
 * DashboardHeader - Top header with logo, brewery selector, and navigation
 */
const DashboardHeader = ({ account, breweries, selectedBreweryId, setSelectedBreweryId, setCurrentView }) => {
  const [showBreweryDropdown, setShowBreweryDropdown] = useState(false);
  const dropdownRef = useRef(null);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowBreweryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const selectedBrewery = breweries?.find(b => b.id === selectedBreweryId);
  
  return (
    <div className="bg-gradient-to-r from-amber-600 to-amber-700 text-white shadow-lg h-24">
      <div className="max-w-6xl mx-auto flex items-center justify-between h-full px-6">
        <div className="flex items-center gap-3 h-full">
          {/* KegOps Logo */}
          <img src="/kegops-logo.png" alt="KegOps" className="h-full object-contain" />
          <div className="flex flex-col">
            <h1 className="text-3xl font-bold">Dashboard</h1>
            {/* Brewery Selector */}
            {breweries && breweries.length > 0 && (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setShowBreweryDropdown(!showBreweryDropdown)}
                  className="flex items-center gap-1 text-sm text-amber-100 hover:text-white transition"
                >
                  {selectedBrewery?.name || 'Select Brewery'}
                  <ChevronDown className="w-4 h-4" />
                </button>
                
                {/* Dropdown Menu */}
                {showBreweryDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-48 bg-white text-gray-900 rounded shadow-lg z-50">
                    {breweries.map(brewery => (
                      <button
                        key={brewery.id}
                        onClick={() => {
                          setSelectedBreweryId(brewery.id);
                          setShowBreweryDropdown(false);
                        }}
                        className={`w-full text-left px-4 py-2 hover:bg-amber-50 transition ${
                          brewery.id === selectedBreweryId ? 'bg-amber-100 font-semibold' : ''
                        }`}
                      >
                        <div className="font-medium">{brewery.name}</div>
                        <div className="text-xs text-gray-500">
                          {brewery.userRole === 'bar_owner' && '👑 Owner'}
                          {brewery.userRole === 'super_admin' && '🔑 Super Admin'}
                          {brewery.userRole === 'bar_manager' && '🔧 Manager'}
                          {brewery.userRole === 'bartender' && '🍺 Bartender'}
                          {brewery.userRole === 'bar_back' && '📦 Bar Back'}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {/* Brewery Logo - Centered */}
        {selectedBrewery?.logo_url && (
          <img src={selectedBrewery.logo_url} alt="Brewery" className="h-20 w-20 object-contain rounded-full" />
        )}
        <div className="flex gap-2">
          <button 
            onClick={() => setCurrentView('account')} 
            className="flex items-center gap-2 bg-amber-800 hover:bg-amber-900 px-3 py-2 rounded transition"
          >
            <User className="w-4 h-4" /><span className="hidden md:inline">Account</span>
          </button>
          <button 
            onClick={() => setCurrentView('settings')} 
            className="flex items-center gap-2 bg-amber-800 hover:bg-amber-900 px-3 py-2 rounded transition"
          >
            <Settings className="w-4 h-4" /><span className="hidden md:inline">Settings</span>
          </button>
          <button 
            onClick={async () => await supabase.auth.signOut()}
            className="flex items-center gap-2 bg-red-600 hover:bg-red-700 px-3 py-2 rounded transition"
            title="Sign out"
          >
            <LogOut className="w-4 h-4" /><span className="hidden md:inline">Logout</span>
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * TabNavigation - Dashboard tab buttons
 */
const TabNavigation = ({ dashboardTab, setDashboardTab, kegsCount }) => {
  const tabs = [
    { id: 'kegs', label: `Kegs (${kegsCount})` },
    { id: 'beers', label: 'On Tap' },
    { id: 'on-deck', label: 'On Deck' },
    { id: 'maintenance', label: 'Maintenance' },
    { id: 'previous-beers', label: 'Previous Beers' }
  ];

  return (
    <div className="flex gap-2 mb-6 border-b border-gray-200 dark:border-gray-700">
      {tabs.map(tab => (
        <button
          key={tab.id}
          onClick={() => setDashboardTab(tab.id)}
          className={`px-6 py-3 font-medium transition ${
            dashboardTab === tab.id 
              ? 'border-b-2 border-amber-600 text-amber-600' 
              : 'text-gray-600 dark:text-gray-400 hover:text-amber-600 dark:hover:text-amber-400'
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
};

/**
 * FilterDropdown - Reusable dropdown filter component
 */
const FilterDropdown = ({ 
  label, 
  options, 
  selectedValues, 
  onToggle, 
  isOpen, 
  setIsOpen, 
  dropdownRef 
}) => (
  <div className="relative" ref={dropdownRef}>
    <div 
      className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 cursor-pointer hover:border-amber-500 transition" 
      onClick={() => setIsOpen(!isOpen)}
    >
      <div className="text-sm">
        {selectedValues.length === 0 ? label : `${selectedValues.length} selected`}
      </div>
    </div>
    {isOpen && (
      <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded shadow-lg max-h-60 overflow-y-auto">
        {options.map(option => {
          const value = typeof option === 'object' ? option.value : option;
          return (
            <label key={value} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer text-gray-900 dark:text-gray-100">
              <input 
                type="checkbox" 
                checked={selectedValues.includes(value)}
                onChange={() => onToggle(value)}
                className="w-4 h-4"
              />
              <span className="text-sm">{value}</span>
            </label>
          );
        })}
        <div className="border-t border-gray-300 dark:border-gray-600 p-2">
          <button 
            onClick={() => setIsOpen(false)}
            className="w-full bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 py-1 rounded text-sm hover:bg-gray-300 dark:hover:bg-gray-500"
          >
            Close
          </button>
        </div>
      </div>
    )}
  </div>
);

/**
 * FilterBar - Search and filter controls for kegs tab
 */
const FilterBar = ({
  searchTerm,
  setSearchTerm,
  filterTapStatus,
  filterCleanStatus,
  filterLocation,
  setFilterLocation,
  showTapDropdown,
  setShowTapDropdown,
  showCleanDropdown,
  setShowCleanDropdown,
  toggleTapStatusFilter,
  toggleCleanStatusFilter,
  locations,
  tapDropdownRef,
  cleanDropdownRef
}) => (
  <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow mb-6">
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      {/* Search Input */}
      <div className="relative">
        <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
        <input 
          type="text" 
          placeholder="Search..." 
          value={searchTerm} 
          onChange={(e) => setSearchTerm(e.target.value)} 
          className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-amber-500 focus:border-transparent" 
        />
      </div>
      
      {/* Tap Status Filter */}
      <FilterDropdown
        label="All Tap Status"
        options={TAP_STATUSES}
        selectedValues={filterTapStatus}
        onToggle={toggleTapStatusFilter}
        isOpen={showTapDropdown}
        setIsOpen={setShowTapDropdown}
        dropdownRef={tapDropdownRef}
      />
      
      {/* Clean Status Filter */}
      <FilterDropdown
        label="All Clean Status"
        options={CLEANING_STATUSES}
        selectedValues={filterCleanStatus}
        onToggle={toggleCleanStatusFilter}
        isOpen={showCleanDropdown}
        setIsOpen={setShowCleanDropdown}
        dropdownRef={cleanDropdownRef}
      />
      
      {/* Location Filter */}
      <select 
        value={filterLocation} 
        onChange={(e) => setFilterLocation(e.target.value)} 
        className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
      >
        <option value="all">All Locations</option>
        {getSelectableLocations(locations).map(loc => (
          <option key={loc.id} value={loc.name}>{loc.displayName}</option>
        ))}
      </select>
    </div>
  </div>
);

/**
 * QuickFilterBar - Radio buttons for quick keg filtering + Add Keg button
 */
const QuickFilterBar = ({ quickFilter, onQuickFilterChange, onAddKeg }) => (
  <div className="flex gap-4 mb-4">
    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow flex-1">
      <div className="flex items-center gap-6">
        <span className="font-medium text-gray-900 dark:text-gray-100">Quick Filter:</span>
        {[
          { value: 'all', label: 'All Kegs' },
          { value: 'empty', label: 'Empty Kegs' },
          { value: 'filled', label: 'Filled Kegs' }
        ].map(option => (
          <label key={option.value} className="flex items-center gap-2 cursor-pointer text-gray-900 dark:text-gray-100">
            <input
              type="radio"
              name="quickFilter"
              checked={quickFilter === option.value}
              onChange={() => onQuickFilterChange(option.value)}
              className="w-4 h-4 text-amber-600"
            />
            <span className="text-sm">{option.label}</span>
          </label>
        ))}
      </div>
    </div>
    
    <button 
      onClick={onAddKeg} 
      className="flex items-center gap-2 bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 transition shadow flex-shrink-0"
    >
      <Plus className="w-5 h-5" />
      <span className="font-medium">Add Keg</span>
    </button>
  </div>
);

// ============================================================
// MAIN COMPONENT
// ============================================================

/**
 * DashboardView - Main dashboard component
 * @description Renders the main dashboard with tabbed navigation.
 * Tab content is rendered from extracted components for maintainability.
 */
export const DashboardView = ({
  // Account data
  account,
  breweries,
  selectedBreweryId,
  setSelectedBreweryId,
  // Tab state
  dashboardTab,
  setDashboardTab,
  // Keg data
  kegs,
  filteredKegs,
  // Filter state
  searchTerm,
  setSearchTerm,
  filterTapStatus,
  filterCleanStatus,
  filterLocation,
  setFilterLocation,
  showTapDropdown,
  setShowTapDropdown,
  showCleanDropdown,
  setShowCleanDropdown,
  toggleTapStatusFilter,
  toggleCleanStatusFilter,
  quickFilter,
  onQuickFilterChange,
  // Location data
  locations,
  beersByLocation,
  onDeckBeersByLocation,
  expandedLocations,
  toggleLocationExpansion,
  // Settings
  settings,
  // Utility functions
  calculateAverageRating,
  getTotalRatings,
  renderStars,
  // Event handlers
  onAddKeg,
  onViewKeg,
  onOpenQRModal,
  onEditKeg,
  onDeleteKeg,
  onOpenRatingModal,
  onViewHistory,
  onOpenMaintenanceModal,
  setCurrentView
}) => {
  // Refs for click-outside dropdown detection
  const tapDropdownRef = useRef(null);
  const cleanDropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tapDropdownRef.current && !tapDropdownRef.current.contains(event.target)) {
        setShowTapDropdown(false);
      }
      if (cleanDropdownRef.current && !cleanDropdownRef.current.contains(event.target)) {
        setShowCleanDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [setShowTapDropdown, setShowCleanDropdown]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <DashboardHeader 
        account={account} 
        breweries={breweries}
        selectedBreweryId={selectedBreweryId}
        setSelectedBreweryId={setSelectedBreweryId}
        setCurrentView={setCurrentView} 
      />

      <div className="max-w-6xl mx-auto p-6">
        {/* Tab Navigation */}
        <TabNavigation 
          dashboardTab={dashboardTab} 
          setDashboardTab={setDashboardTab}
          kegsCount={kegs.length}
        />

        {/* Kegs Tab */}
        {dashboardTab === 'kegs' && (
          <>
            <FilterBar
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              filterTapStatus={filterTapStatus}
              filterCleanStatus={filterCleanStatus}
              filterLocation={filterLocation}
              setFilterLocation={setFilterLocation}
              showTapDropdown={showTapDropdown}
              setShowTapDropdown={setShowTapDropdown}
              showCleanDropdown={showCleanDropdown}
              setShowCleanDropdown={setShowCleanDropdown}
              toggleTapStatusFilter={toggleTapStatusFilter}
              toggleCleanStatusFilter={toggleCleanStatusFilter}
              locations={locations}
              tapDropdownRef={tapDropdownRef}
              cleanDropdownRef={cleanDropdownRef}
            />
            <QuickFilterBar
              quickFilter={quickFilter}
              onQuickFilterChange={onQuickFilterChange}
              onAddKeg={onAddKeg}
            />
            <DashboardKegsTab
              filteredKegs={filteredKegs}
              settings={settings}
              onViewKeg={onViewKeg}
              onOpenQRModal={onOpenQRModal}
              onEditKeg={onEditKeg}
              onDeleteKeg={onDeleteKeg}
            />
          </>
        )}

        {/* Beers on Tap Tab */}
        {dashboardTab === 'beers' && (
          <DashboardBeersTab
            locations={locations}
            beersByLocation={beersByLocation}
            expandedLocations={expandedLocations}
            toggleLocationExpansion={toggleLocationExpansion}
            account={account}
            calculateAverageRating={calculateAverageRating}
            getTotalRatings={getTotalRatings}
            renderStars={renderStars}
            onOpenRatingModal={onOpenRatingModal}
            onViewKeg={onViewKeg}
          />
        )}

        {/* On Deck Tab */}
        {dashboardTab === 'on-deck' && (
          <DashboardOnDeckTab
            locations={locations}
            onDeckBeersByLocation={onDeckBeersByLocation}
            expandedLocations={expandedLocations}
            toggleLocationExpansion={toggleLocationExpansion}
            account={account}
            onOpenRatingModal={onOpenRatingModal}
            onViewKeg={onViewKeg}
          />
        )}

        {/* Maintenance Tab */}
        {dashboardTab === 'maintenance' && (
          <DashboardMaintenanceTab
            kegs={kegs}
            locations={locations}
            settings={settings}
            selectedBrewery={breweries?.find(b => b.id === selectedBreweryId)}
            onOpenMaintenance={onOpenMaintenanceModal}
            onNavigateToSettings={() => setCurrentView('settings')}
          />
        )}

        {/* Previous Beers Tab */}
        {dashboardTab === 'previous-beers' && (
          <DashboardHistoryTab
            kegs={kegs}
            account={account}
            calculateAverageRating={calculateAverageRating}
            getTotalRatings={getTotalRatings}
            renderStars={renderStars}
            onViewHistory={onViewHistory}
          />
        )}
      </div>
    </div>
  );
};

export default DashboardView;




